import { useState } from "react";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { HardDriveDownload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, useApi } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BackupTable, ScheduleCard, RestoreDialog, RunStatus, UploadButton, usePolling, fmtSize } from "@/components/backup";

export default function BackupView() { const { t } = useT();
  const { can } = useAuth();
  const data = useApi("/backup");
  const [restoring, setRestoring] = useState(null);
  const d = data.data || {};
  const running = d.run?.phase === "running";
  usePolling(data.reload, running);
  const canConfigure = can("core.settings", "configure");
  const runNow = async () => { try { await api.post("/backup/run", { note: "Manual" }); toast.success(t("lbl.backup_started")); data.reload(); } catch (e) { toast.error(errMsg(e)); } };
  const saveSchedule = async (s) => { try { await api.put("/backup/schedule", s); toast.success(t("lbl.schedule_saved")); data.reload(); } catch (e) { toast.error(errMsg(e)); } };
  const restore = async (opts) => { try { await api.post(`/backup/${restoring.id}/restore`, opts); toast.success(t("lbl.restore_started")); setRestoring(null); data.reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader eyebrow={t("lbl.workspace")} title={t("lbl.backup_recovery")} subtitle="Full copies of your workspace — every record of every module plus uploaded files — that you can download, keep off-site and restore at any time." testId="backup-page"
        actions={canConfigure && <div className="flex gap-2"><UploadButton url="/backup/upload" accept=".zip" onDone={data.reload} testId="backup-upload-btn" /><Button className="bg-blue-600 hover:bg-blue-700" data-testid="backup-run-btn" disabled={running} onClick={runNow}><HardDriveDownload className="h-4 w-4 mr-1" />{running ? "Running…" : "Back up now"}</Button></div>} />
      <div className="space-y-4">
        <RunStatus run={d.run} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="grid grid-cols-3 gap-3">{[["Backups", d.items?.length ?? "—"], ["Storage used", fmtSize(d.storage_used)], ["Schedule", d.schedule?.enabled ? `${d.schedule.frequency} · ${d.schedule.hour}:00 UTC` : "off"]].map(([k, v]) => <div key={k} className="bg-card border rounded-lg p-4" data-testid={`backup-kpi-${k.toLowerCase().replace(/ /g, "-")}`}><div className="eyebrow">{k}</div><div className="font-mono text-sm mt-1 capitalize">{v}</div></div>)}</div>
            <BackupTable items={d.items || []} base="/backup" onChanged={data.reload} onRestore={setRestoring} />
          </div>
          <div className="space-y-4">
            {canConfigure && <ScheduleCard value={d.schedule} onSave={saveSchedule} />}
            <div className="bg-card border rounded-lg p-5 text-xs text-muted-foreground space-y-2" data-testid="backup-help">
              <div className="eyebrow text-foreground">{t("lbl.what_is_included")}</div>
              <p>All records of this workspace across every module (contacts, invoices, journal entries, stock, HR, POS, projects…), your companies, users and roles, settings, custom fields and every uploaded file.</p>
              <p>Restoring never deletes users, roles or your licence, so you cannot lock yourself out. Every restore is written to the audit log.</p>
            </div>
          </div>
        </div>
      </div>
      <RestoreDialog backup={restoring} onClose={() => setRestoring(null)} onConfirm={restore} />
    </div>
  );
}
